import cls from "@fvilers/cls";
import { InputIcon } from "@imi/icons/InputIcon.js";
import { ValidationResult } from "@src/core/engine/validationResults/buildValidationResult";
import { useParseValidationResult } from "@src/core/engine/validationResults/useParseValidationResult";
import { useAppSelector } from "@src/core/store";
import { useUnitConversion } from "@src/shared/hooks/useUnitConversion";
import { useStore } from "@tanstack/react-form";
import { Row, RowData, TableFeatures } from "@tanstack/react-table";
import { get } from "radashi";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useBatchSelectionFiltersForm } from "../../../hooks/useBatchSelectionFiltersForm";
import { BatchSelectionRow } from "../../../store/types";
import styles from "../BatchSelectionDataTable.module.css";
import { ImportDropzone } from "../components/ImportDropzone/ImportDropzone";
import { SuggestionsStatus } from "../components/SuggestionsStatus";
import { TableGroupHeader } from "../components/TableGroupHeader";
import {
  isDifferentialTemperatureValid,
  isFlowValid,
  isPowerValid,
  pipeSizeOptions,
} from "../helpers/getColumnsValidation";
import { getPipeSizeWarning } from "../helpers/getPipeSizeWarning";
import { columnHelper } from "./useBatchSelectionDataTableColumns";

export function useBatchSelectionInputColumns(
  form: ReturnType<typeof useBatchSelectionFiltersForm>["form"],
) {
  const { t } = useTranslation("product", {
    keyPrefix: "selection.batch.dataTable",
  });
  const parseValidationResult = useParseValidationResult({
    fieldsT: { t },
    ignoreConversion: true,
    maxDecimals: 3,
  });
  const { getUnitSymbol } = useUnitConversion();
  const useFlowOrPower = useStore(
    form.store,
    (state) => state.values.useFlowOrPower,
  );
  const hasData = useAppSelector(
    (state) => state.batchSelection.data.length > 1,
  );

  const setUseFlowOrPower = useCallback(
    (value: string) => {
      if (value !== "flow" && value !== "power") return;
      form.setFieldValue("useFlowOrPower", value);
    },
    [form],
  );

  const parseValidation = (args: ValidationResult) => {
    return {
      message: parseValidationResult(args),
      severity: args.severity,
    };
  };

  return useMemo(
    () =>
      columnHelper.group({
        columns: columnHelper.columns([
          columnHelper.display({
            cell: (info) => <SuggestionsStatus row={info.row.original} />,
            enableHiding: false,
            header: t("columns.status"),
            id: "status",
            meta: {
              cell: { className: styles.InputCell },
              draggable: false,
              header: { className: styles.InputHeader },
            },
            size: 40,
          }),
          columnHelper.accessor("reference", {
            editable: true,
            header: t("columns.reference"),
            id: "reference",
            inputCell: {
              getValue: (row) => getRowData(row).reference,
              type: "text",
            },
            meta: {
              cell: { className: styles.InputCell },
              header: { className: styles.InputHeader },
            },
            size: 180,
          }),
          columnHelper.accessor("additionalReference", {
            editable: true,
            header: t("columns.additionalReference"),
            id: "additionalReference",
            inputCell: {
              getValue: (row) => getRowData(row).additionalReference,
              type: "text",
            },
            meta: {
              cell: { className: styles.InputCell },
              header: { className: styles.InputHeader },
            },
            size: 180,
          }),
          columnHelper.accessor("pipeSize", {
            editable: true,
            header: t("columns.pipeSize"),
            id: "pipeSize",
            inputCell: {
              getValue: (row) => getRowData(row).pipeSize ?? "",
              options: pipeSizeOptions,
              type: "select",
              validate: (_value, row) => {
                const rowData = row.original as BatchSelectionRow;
                if (rowData.status?.path === "pipeSize") {
                  return {
                    message:
                      getPipeSizeWarning(t, rowData) ||
                      parseValidationResult(rowData.status, {
                        fieldsT: { prefix: "status" },
                      }),
                    severity: rowData.status?.severity,
                  };
                }
                return null;
              },
            },
            meta: {
              cell: { className: styles.InputCell },
              header: {
                className: cls(styles.InputHeader, styles.PipeSizeHeader),
              },
            },
            size: 130,
            sortFn: (rowA, rowB) => {
              const pipeSizeA = rowA.original.pipeSize ?? "";
              const pipeSizeB = rowB.original.pipeSize ?? "";
              const indexA = pipeSizeOptions.findIndex(
                (option) => option.value === pipeSizeA,
              );
              const indexB = pipeSizeOptions.findIndex(
                (option) => option.value === pipeSizeB,
              );
              return indexA - indexB;
            },
          }),
          columnHelper.accessor("dp", {
            editable: true,
            header: t("columns.dp", {
              interpolation: { escapeValue: false },
              unit: getUnitSymbol("PHYS_DIFFPRESS"),
            }),
            id: "dp",
            inputCell: {
              getValue: (row) => getRowData(row).dp ?? "",
              type: "text",
              validate: (value, row) => {
                const rowData = row.original as BatchSelectionRow;
                const flowError = get<ValidationResult>(
                  rowData,
                  "inputErrors.flow",
                );
                if (flowError) return parseValidation(flowError);
                const result = isFlowValid(value);
                if (result === true) return null;
                return parseValidation(result);
              },
            },
            meta: {
              cell: { className: styles.InputCell },
              header: { className: styles.InputHeader },
            },
            size: 120,
          }),
          ...(useFlowOrPower === "flow"
            ? [
                columnHelper.accessor("flow", {
                  editable: true,
                  enableHiding: false,
                  header: t("columns.flow", {
                    interpolation: { escapeValue: false },
                    unit: getUnitSymbol("PHYS_FLOW"),
                  }),
                  id: "flow",
                  inputCell: {
                    getValue: (row) => getRowData(row).flow ?? "",
                    type: "text",
                    validate: (value, row) => {
                      const rowData = row.original as BatchSelectionRow;
                      const flowError = get<ValidationResult>(
                        rowData,
                        "inputErrors.flow",
                      );
                      if (flowError) return parseValidation(flowError);
                      const result = isFlowValid(value);
                      if (result === true) return null;
                      return parseValidation(result);
                    },
                  },
                  meta: {
                    cell: { className: styles.InputCell },
                    header: { className: styles.InputHeader },
                  },
                  size: 120,
                }),
              ]
            : [
                columnHelper.accessor("power", {
                  editable: true,
                  enableHiding: false,
                  header: t("columns.power", {
                    interpolation: { escapeValue: false },
                    unit: getUnitSymbol("PHYS_TH_POWER"),
                  }),
                  id: "power",
                  inputCell: {
                    getValue: (row) => getRowData(row).power ?? "",
                    type: "text",
                    validate: (value, row) => {
                      const rowData = row.original as BatchSelectionRow;
                      const powerError = get<ValidationResult>(
                        rowData,
                        "inputErrors.power",
                      );
                      if (powerError) return parseValidation(powerError);
                      const result = isPowerValid(value);
                      if (result === true) return null;
                      return parseValidation(result);
                    },
                  },
                  meta: {
                    cell: { className: styles.InputCell },
                    header: { className: styles.InputHeader },
                  },
                  size: 120,
                }),
                columnHelper.accessor("differentialTemperature", {
                  editable: true,
                  enableHiding: false,
                  header: t("columns.differentialTemperature", {
                    interpolation: { escapeValue: false },
                    unit: getUnitSymbol("PHYS_DIFFTEMP"),
                  }),
                  id: "differentialTemperature",
                  inputCell: {
                    getValue: (row) =>
                      getRowData(row).differentialTemperature ?? "",
                    type: "text",
                    validate: (value, row) => {
                      const rowData = row.original as BatchSelectionRow;
                      const deltaTError = get<ValidationResult>(
                        rowData,
                        "inputErrors.differentialTemperature",
                      );
                      if (deltaTError) return parseValidation(deltaTError);
                      const result = isDifferentialTemperatureValid(value);
                      if (result === true) return null;
                      return parseValidation(result);
                    },
                  },
                  meta: {
                    cell: { className: styles.InputCell },
                    header: { className: styles.InputHeader },
                  },
                  size: 120,
                }),
              ]),
          columnHelper.accessor("quantity", {
            editable: true,
            enableHiding: false,
            header: t("columns.quantity"),
            id: "quantity",
            inputCell: {
              getValue: (row) => getRowData(row).quantity,
              min: 1,
              type: "stepper",
            },
            meta: {
              cell: { className: styles.InputCell },
              header: { className: styles.InputHeader },
            },
            size: 120,
          }),
        ]),
        enableHiding: false,
        enableSorting: false,
        footer: !hasData
          ? (header) => (
              <ImportDropzone
                header={header}
                setUseFlowOrPower={setUseFlowOrPower}
                useFlowOrPower={useFlowOrPower}
              />
            )
          : undefined,
        header: ({ column }) => (
          <TableGroupHeader
            hiddenColumns={column
              .getLeafColumns()
              .filter((c) => !c.getIsVisible())
              .map((column) => ({ column }))}
            icon={<InputIcon />}
            label={t("columns.groups.inputs")}
          />
        ),
        id: "inputs",
        meta: {
          draggable: false,
          header: { className: styles.TableGroupHeader },
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getUnitSymbol, hasData, setUseFlowOrPower, t, useFlowOrPower],
  );
}

function getRowData(row: Row<TableFeatures, RowData>) {
  return row.original as BatchSelectionRow;
}
